import { computed, observable, reaction, runInAction } from "mobx"

import { withGlobalLoader } from "@/lib/context.ts"
import { Model } from "@/lib/Model.ts"
import { optimisticAction } from "@/lib/std.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { TaskViewId } from "@/routes/tasks/TaskViewId.ts"
import { allScriptsStarted } from "@/routes/tasks/TaskViewScene/TaskPreviewModel.ts"
import { TabId } from "@/routes/tasks/TaskViewScene/TaskTabId.ts"

import type { RouterType } from "@/context.ts"
import type {
  AiAgentTask,
  InitRuntimeConfigTaskState,
  ProcessRunState,
  RuntimeConfig,
} from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { Queries } from "@/lib/queries.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"

export class TaskRuntimeConfigFileModel extends Model {
  private readonly router: RouterType
  private readonly context: OrgContext
  private readonly queries: Queries
  readonly taskViewModel: TaskViewModel
  readonly task: AiAgentTask | null

  readonly runtimeState = observable.box<InitRuntimeConfigTaskState | null | undefined>(null)

  readonly rootFolder = observable.box("")
  readonly previewPort = observable.box(3000)
  readonly beforeCommand = observable.box("")
  readonly previewCommands = observable.box<RuntimeConfig.RunAppCommand[]>([])

  readonly runtimeEnvDescription = observable.box<string | null>(null)

  readonly isEnvActive = computed(() => {
    return this.taskViewModel.previewProcessesState?.get()?.className == "Running"
  })

  readonly isAnyScriptRunning = computed(() => {
    const processState = this.taskViewModel.previewProcessesState?.get()
    if (processState == null || processState.className != "Running") {
      return false
    }

    if (processState.warmupScriptState.className == "Running") {
      return true
    }

    return processState.processStates
      .map(({ second: processRunState }) => processRunState)
      .some(processRunState => processRunState.className === "Running" && !processRunState.ready)
  })

  readonly previewProcessesState = computed(() => this.taskViewModel.previewProcessesState.get())

  readonly previewCommandsState = computed(() => {
    const runtimeConfig = this.taskViewModel.runtimeConfig.get()
    const processState = this.previewProcessesState.get()
    if (processState?.className !== "Running") {
      return runtimeConfig?.previewCommands?.map((cmd) => ({
        command: cmd,
        state: { className: "NotStarted" } satisfies ProcessRunState.NotStarted
      })) || []
    }

    return runtimeConfig?.previewCommands?.map((cmd) => {
      const state = processState.processStates.find((p) => p.first.id == cmd.id)?.second
      return {
        command: cmd,
        state: state || { className: "NotStarted" } satisfies ProcessRunState.NotStarted
      }
    }) || []
  })

  constructor(params: {
    readonly context: OrgContext
    readonly queries: Queries,
    readonly router: RouterType,
    readonly taskViewModel: TaskViewModel,
    readonly task: AiAgentTask | null
  }) {
    super("TaskRuntimeConfigFileModel")

    this.router = params.router
    this.context = params.context
    this.queries = params.queries
    this.taskViewModel = params.taskViewModel
    this.task = params.task

    this.lifetime.add(reaction(
      () => this.taskViewModel.previewProcessesState.get(),
      (current, prev) => {
        if (allScriptsStarted(current) && !allScriptsStarted(prev)) {
          this.taskViewModel.invalidateTaskQuery(this.taskViewModel.taskId)
          void this.router.navigate(Navigator.task(this.context.orgId, this.taskViewModel.project.get().key, this.taskViewModel.taskId, {
            view: TaskViewId.Full,
            tab: TabId.Preview
          }))
        }
      }), "TaskRuntimeConfigFileModel::switchToPreviewTab")

    const runtimeConfig = this.taskViewModel.runtimeConfig.get()

    runInAction(() => {
      this.runtimeState.set(this.task?.initRuntimeConfigState)

      this.rootFolder.set(runtimeConfig?.rootFolder || "")
      this.previewPort.set(runtimeConfig?.previewPort || 3000)
      this.beforeCommand.set(runtimeConfig?.beforeCommand || "")
      this.previewCommands.set(runtimeConfig?.previewCommands || [])
    })

    this.lifetime.add(reaction(
      () => (
        {
          runtimeConfig: this.taskViewModel.runtimeConfig.get()
        }
      ),
      ({ runtimeConfig }) => {
        this.rootFolder.set(runtimeConfig?.rootFolder || "")
        this.previewPort.set(runtimeConfig?.previewPort || 3000)
        this.beforeCommand.set(runtimeConfig?.beforeCommand || "")
        this.previewCommands.set(runtimeConfig?.previewCommands || [])
      }
    ), "TaskRuntimeConfigFileModel:runtimeConfig")

    // Fetch runtime environment description for this task
    const taskId = this.task?.id
    if (taskId) {
      void this.queries.getRuntimeEnvironmentDescription(taskId)
        .then(text => runInAction(() => this.runtimeEnvDescription.set(text)))
    }
  }

  readonly handleBeforeCommandChange = async (value: string) => {
    const current = this.beforeCommand.get()
    await optimisticAction(
      () => this.beforeCommand.set(value),
      () => this.queries.apiClient.saveBeforeCommand(this.taskViewModel.taskId, value),
      () => this.beforeCommand.set(current)
    )
  }

  readonly handlePreviewRunCommandChange = async (index: number, value: string) => {
    const command = this.previewCommands.get().at(index)
    if (!command) return

    const previousCommands = this.previewCommands.get().slice()
    const nextCommands = previousCommands.slice()
    if (index >= 0 && index < nextCommands.length) {
      nextCommands[index] = { ...nextCommands[index], command: value }
    }

    await optimisticAction(
      () => this.previewCommands.set(nextCommands),
      () => this.queries.apiClient.savePreviewCommand(this.taskViewModel.taskId, command.id, value),
      () => this.previewCommands.set(previousCommands)
    )
  }

  readonly handleRecreatePreviewRuntime = async () => {
    await withGlobalLoader(this.context, this.queries.apiClient.recreatePreview(this.taskViewModel.taskId))
  }

  readonly handleRunBeforeCommand = async () => {
    await withGlobalLoader(this.context, this.queries.apiClient.verifyBeforeCommand(this.taskViewModel.taskId))
  }

  readonly handleRunPreviewCommand = async (index: number) => {
    const cmd = this.previewCommands.get()[index]
    if (!cmd) return
    await withGlobalLoader(this.context, this.queries.apiClient.verifyPreviewCommand(this.taskViewModel.taskId, cmd.id))
  }

  readonly handleStopBeforeCommand = async () => {
    await withGlobalLoader(this.context, this.queries.apiClient.stopBeforeCommand(this.taskViewModel.taskId))
  }

  readonly handleStopPreviewCommand = async (commandId: string) => {
    await withGlobalLoader(this.context, this.queries.apiClient.stopPreviewCommand(this.taskViewModel.taskId, commandId))
  }

  readonly handleAddPreviewCommand = async (id: string, title: string, script: string) => {
    await withGlobalLoader(this.context, this.queries.apiClient.addPreviewCommand(this.taskViewModel.taskId, id, title, script))
  }

  readonly handleRemovePreviewCommand = async (commandId: string) => {
    await withGlobalLoader(this.context, this.queries.apiClient.removePreviewCommand(this.taskViewModel.taskId, commandId))
  }
}
